<template>
  <div class="wowza-target-config">
    <el-form-item label="Entry Name" prop="wowzaTargetConfig.entryName" required>
      <el-input v-model="localConfig.entryName" size="small" placeholder="Target Name"></el-input>
    </el-form-item>

    <el-form-item label="Profile" prop="wowzaTargetConfig.profile" required>
      <el-select v-model="localConfig.profile" size="small" @change="onProfileChange" placeholder="Select Profile">
        <el-option label="RTMP" value="rtmp"></el-option>
        <el-option label="YouTube (RTMP)" value="youtube"></el-option>
        <el-option label="MPEG-TS" value="mpegts"></el-option>
        <el-option label="RTP" value="rtp"></el-option>
        <el-option label="SRT" value="srt"></el-option>
      </el-select>
    </el-form-item>

    <div v-if="localConfig.profile">
      <el-divider content-position="left">{{ getProfileName(localConfig.profile) }} Configuration</el-divider>

      <!-- Common Fields -->
      <div v-if="['rtmp', 'youtube'].includes(localConfig.profile)">
        <el-form-item label="Application Name" prop="wowzaTargetConfig.application">
          <el-input v-model="localConfig.application" size="small" placeholder="Application Name"></el-input>
        </el-form-item>
      </div>

      <el-form-item label="Host" prop="wowzaTargetConfig.host" required>
        <el-input v-model="localConfig.host" size="small" placeholder="Destination Host"></el-input>
      </el-form-item>

      <el-row :gutter="14" v-if="localConfig.profile !== 'rtp'">
        <el-col :span="12">
          <el-form-item label="Port" prop="wowzaTargetConfig.port" required>
            <el-input v-model.number="localConfig.port" size="small" type="number" placeholder="Port"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12" v-if="['rtmp', 'youtube', 'mpegts'].includes(localConfig.profile)">
          <el-form-item label="Stream Name" prop="wowzaTargetConfig.streamName" required>
             <el-input v-model="localConfig.streamName" size="small" placeholder="Stream Name"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- RTP specific -->
      <el-row :gutter="14" v-if="localConfig.profile === 'rtp'">
        <el-col :span="8">
          <el-form-item label="Video Port" prop="wowzaTargetConfig.videoPort" required>
            <el-input v-model="localConfig.videoPort" size="small" placeholder="Video Port"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Audio Port" prop="wowzaTargetConfig.audioPort" required>
             <el-input v-model="localConfig.audioPort" size="small" placeholder="Audio Port"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Stream Name" prop="wowzaTargetConfig.streamName" required>
             <el-input v-model="localConfig.streamName" size="small" placeholder="Stream Name"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      
      <!-- Optional TTL / Timeouts -->
      <el-row :gutter="14" v-if="['mpegts', 'rtp'].includes(localConfig.profile)">
        <el-col :span="12">
          <el-form-item label="Time To Live (TTL)" prop="wowzaTargetConfig.timeToLive">
             <el-input-number v-model="localConfig.timeToLive" size="small" :min="1" :max="255"></el-input-number>
          </el-form-item>
        </el-col>
        <el-col :span="12" v-if="localConfig.profile === 'rtp'">
          <el-form-item label="Stream Wait Timeout" prop="wowzaTargetConfig.streamWaitTimeout">
             <el-input-number v-model="localConfig.streamWaitTimeout" size="small" :step="1000"></el-input-number>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- SRT specific -->
      <el-collapse v-if="localConfig.profile === 'srt'" style="margin-bottom: 20px;">
        <el-collapse-item title="Advanced SRT Settings" name="1">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
            <el-form-item label="Latency (ms)">
              <el-input-number v-model="localConfig.srtLatency" size="small" controls-position="right"></el-input-number>
            </el-form-item>
            <el-form-item label="Stream ID">
              <el-input v-model="localConfig.srtStreamId" size="small"></el-input>
            </el-form-item>
            <el-form-item label="Send Buffer Size">
              <el-input-number v-model="localConfig.srtSendBufferSize" size="small" controls-position="right"></el-input-number>
            </el-form-item>
            <el-form-item label="UDP Send Buffer Size">
              <el-input-number v-model="localConfig.srtSendBufferSizeUDP" size="small" controls-position="right"></el-input-number>
            </el-form-item>
            <el-form-item label="Max Segment Size">
              <el-input-number v-model="localConfig.srtMaximumSegmentSize" size="small" controls-position="right"></el-input-number>
            </el-form-item>
             <el-form-item label="Flight Flag Size">
              <el-input-number v-model="localConfig.srtFlightFlagSize" size="small" controls-position="right"></el-input-number>
            </el-form-item>
            <el-form-item label="Max Bandwidth">
              <el-input-number v-model="localConfig.srtMaximumBandwidth" size="small" controls-position="right"></el-input-number>
            </el-form-item>
             <el-form-item label="Input Bandwidth">
              <el-input-number v-model="localConfig.srtInputBandwidth" size="small" controls-position="right"></el-input-number>
            </el-form-item>
            <el-form-item label="Overhead Bandwidth">
              <el-input-number v-model="localConfig.srtOverheadBandwidth" size="small" controls-position="right"></el-input-number>
            </el-form-item>
            <el-form-item label="Connect Timeout (ms)">
              <el-input-number v-model="localConfig.srtConnectTimeout" size="small" controls-position="right" :step="1000"></el-input-number>
            </el-form-item>
             <el-form-item label="Peer Idle Timeout (ms)">
              <el-input-number v-model="localConfig.srtPeerIdleTimeout" size="small" controls-position="right" :step="1000"></el-input-number>
            </el-form-item>
            <el-form-item label="Timestamp Based Delivery">
              <el-switch v-model="localConfig.srtTimestampBasedDeliveryMode" size="small"></el-switch>
            </el-form-item>
            <el-form-item label="Too Late Packet Drop">
              <el-switch v-model="localConfig.srtTooLatePacketDrop" size="small"></el-switch>
            </el-form-item>
          </div>
        </el-collapse-item>
      </el-collapse>

      <el-form-item label="Source Stream Name" prop="wowzaTargetConfig.sourceStreamName">
        <el-input v-model="localConfig.sourceStreamName" size="small" placeholder="Source Stream Name (Original Workflow)"></el-input>
      </el-form-item>

      <el-form-item prop="wowzaTargetConfig.enabled">
        <el-switch v-model="localConfig.enabled" size="small" active-text="Target Enabled"></el-switch>
      </el-form-item>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WowzaTargetConfig',
  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({
        entryName: '',
        profile: '',
        host: '',
        port: null,
        streamName: '',
        enabled: true,
        sourceStreamName: ''
      })
    }
  },
  computed: {
    localConfig: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  methods: {
    getProfileName(profile) {
      const names = {
        rtmp: 'RTMP',
        youtube: 'YouTube (RTMP)',
        mpegts: 'MPEG-TS',
        rtp: 'RTP',
        srt: 'SRT'
      };
      return names[profile] || profile;
    },
    onProfileChange(newProfile) {
      // Set defaults based on the selected profile
      const defaults = {
        mpegts: {
          timeToLive: 63
        },
        rtp: {
          timeToLive: 63,
          streamWaitTimeout: 5000
        },
        srt: {
          srtLatency: 400,
          srtTooLatePacketDrop: true,
          srtTimestampBasedDeliveryMode: true,
          srtSendBufferSize: 12058624,
          srtSendBufferSizeUDP: 65536,
          srtMaximumSegmentSize: 1500,
          srtFlightFlagSize: 25600,
          srtMaximumBandwidth: -1,
          srtInputBandwidth: 0,
          srtOverheadBandwidth: 25,
          srtConnectTimeout: 3000,
          srtPeerIdleTimeout: 5000,
          srtStreamId: ''
        }
      };

      if (defaults[newProfile]) {
        this.localConfig = {
          ...this.localConfig,
          ...defaults[newProfile]
        };
      }
    }
  }
}
</script>

<style scoped>
.wowza-target-config {
  background: #fdfdfd;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  margin-top: 10px;
}
</style>
